

import { useState } from 'react';
import { Property, leadService } from '../lib/supabase';

interface PropertyInquiryModalProps {
  property: Property;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function PropertyInquiryModal({ property, isOpen, onClose, onSuccess }: PropertyInquiryModalProps) {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const leadData = {
        name: formData.get('name') as string,
        email: '',
        phone: formData.get('phone') as string || '',
        message: formData.get('message') as string || '',
        source: 'listing_modal',
        property_id: property.id
      };

      await leadService.create(leadData);
      
      setFormStatus('success');
      form.reset();
      setTimeout(() => {
        setFormStatus('idle');
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
      }, 2000);
    } catch (error) {
      console.error('Error submitting property inquiry:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xl z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
        
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Pošaljite upit</h3>
          <div className="text-sm text-gray-600">
            {property.title} • {property.location}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="hidden" name="property_id" value={property.id} />
          
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Ime i prezime *</label>
            <input 
              type="text"
              name="name"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
              placeholder="Vaše ime i prezime"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Kontakt telefon *</label>
            <input 
              type="tel"
              name="phone"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
              placeholder="+385 xx xxx xxxx"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Što Vas interesira? *</label>
            <textarea 
              name="message"
              required
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all resize-none"
              placeholder="Unesite vašu poruku..."
            ></textarea>
          </div>
          
          <div className="flex items-start space-x-3">
            <input 
              type="checkbox"
              id="property-gdpr-consent"
              name="gdpr_consent"
              required
              className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <label htmlFor="property-gdpr-consent" className="text-xs text-gray-600 leading-relaxed">
              Slažem se da se moji podaci koriste isključivo za kontakt i ponudu nekretnina.
            </label>
          </div>
          
          <button 
            type="submit"
            disabled={formStatus === 'submitting'}
            className="w-full bg-red-600 text-white py-3 rounded-xl font-medium hover:bg-red-700 disabled:bg-gray-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {formStatus === 'submitting' ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Šalje se...
              </>
            ) : formStatus === 'success' ? (
              <>
                <i className="ri-check-line mr-2"></i>
                Uspješno poslano!
              </>
            ) : formStatus === 'error' ? (
              <>
                <i className="ri-error-warning-line mr-2"></i>
                Greška - pokušajte ponovo
              </>
            ) : (
              'Pošalji upit'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

